import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import headacheData from "../../data/headache.json";
import styles from "./HeadacheCheckNew.module.css";
import { ArrowBack } from "@mui/icons-material";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  setDoc,
  doc,
  getDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { getUserId } from "../../utils/userId";
import { v4 as uuidv4 } from "uuid";

interface FollowUpQuestion {
  id: string;
  text_key: string;
  options: {
    optionTextKey: string;
    value: number | boolean;
  }[];
  followUpIfYes?: FollowUpQuestion[];
}

interface Question {
  id: string;
  type: string;
  questionTextKey: string;
  options: {
    optionTextKey: string;
    value: number | boolean;
  }[];
  followUpIfYes?: FollowUpQuestion[];
}

interface Answer {
  questionId: string;
  optionId: string;
  value: number | boolean;
}

// New interface for flattened questions
interface FlattenedQuestion {
  id: string;
  questionTextKey: string;
  options: {
    optionTextKey: string;
    value: number | boolean;
  }[];
  parentId?: string;
}

const HeadacheCheckNew: React.FC = () => {
  console.log("HeadacheCheckNew component mounted");
  // console.log("headacheData:", headacheData);

  const navigate = useNavigate();
  const location = useLocation();
  const { docId } = location.state || {};
  const { t } = useTranslation();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [flattenedQuestions, setFlattenedQuestions] = useState<
    FlattenedQuestion[]
  >([]);
  const [testCompleted, setTestCompleted] = useState(false);
  const [savingToFirebase, setSavingToFirebase] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const optionsContainerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPreviousHistory, setHasPreviousHistory] = useState(false);

  const flattenQuestions = (questions: Question[], currentAnswers: Answer[]): FlattenedQuestion[] => {
      let result: FlattenedQuestion[] = [];

      const processQuestion = (question: Question, parentId?: string) => {
        // Add the current question
        result.push({
          id: question.id,
          questionTextKey: question.questionTextKey,
          options: question.options,
          parentId: parentId,
        });

        // Check if this question has follow-up questions and if the answer is "yes"
        if (question.followUpIfYes && question.followUpIfYes.length > 0) {
          const yesAnswer = currentAnswers.find(
            (a) => a.questionId === question.id && a.value === true
          );

          if (yesAnswer) {
            // Process each follow-up question
            question.followUpIfYes.forEach((followUp) => {
              processQuestion(
                {
                  id: followUp.id,
                  type: "followup",
                  questionTextKey: followUp.text_key,
                  options: followUp.options,
                  followUpIfYes: followUp.followUpIfYes,
                },
                question.id
              );
            });
          }
        }
      };

      // Process each main question
      questions.forEach((question) => {
        processQuestion(question);
      });

      return result;
    };

  useEffect(() => {
    const checkPreviousHistory = async () => {
      try {
        const userId = getUserId();
        if (!userId) return; 

        // 1. 현재 docId가 있다면 (이어서 하는 기록) -> 기록이 있는 것으로 간주
        if (docId) {
            setHasPreviousHistory(true);
            return;
        }

        // 2. docId가 없다면, Firestore를 조회하여 이전에 완료된 기록이 있는지 확인
        const docRef = doc(db, "userHeadacheResponse", "dummy-check-for-existence"); // 더미 문서 참조 (실제 문서 ID가 아님)
        const q = query(collection(db, "userHeadacheResponse"), where("userId", "==", userId));
        const querySnapshot = await getDocs(q);
        
        // 기록이 1개라도 있다면 (현재 진행 중인 것을 제외하고)
        if (querySnapshot.docs.length > 0) {
          setHasPreviousHistory(true);
        }
      } catch (error) {
        console.error("Error checking headache history:", error);
      }
    };
    checkPreviousHistory();
  }, [docId]);

  // Load answers from location state or localStorage on component mount
  useEffect(() => {
    if (location.state?.answers) {
      setAnswers(location.state.answers);
      // Set current question index to the last answered question
      setCurrentQuestionIndex(location.state.answers.length);
    } else {
      const savedAnswers = localStorage.getItem("headacheAnswers");
      if (savedAnswers) {
        const parsedAnswers = JSON.parse(savedAnswers);
        setAnswers(parsedAnswers);
        setCurrentQuestionIndex(parsedAnswers.length);
      }
    }
    setIsLoading(false);
  }, [location.state]);

  // Flatten the question structure to include follow-up questions
  useEffect(() => {
    setFlattenedQuestions(flattenQuestions(headacheData.questions, answers));
  }, [answers]);

  // Initialize refs array
  useEffect(() => {
    optionsContainerRefs.current = optionsContainerRefs.current.slice(
      0,
      flattenedQuestions.length
    );
  }, [flattenedQuestions.length]);

  // Scroll to current question's options container
  useEffect(() => {
    if (
      chatContainerRef.current &&
      optionsContainerRefs.current[currentQuestionIndex]
    ) {
      const currentOptionsElement =
        optionsContainerRefs.current[currentQuestionIndex];
      if (currentOptionsElement) {
        currentOptionsElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [currentQuestionIndex, flattenedQuestions]);

  // Scroll to results when test is completed
  useEffect(() => {
    if (testCompleted && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 500); // Small delay to ensure the results section is rendered
    }
  }, [testCompleted]);

  const getProgressPercentage = (index: number, totalCount: number) => {
    if (totalCount === 0) return 0;
    // (현재 번호 / 그 시점의 전체 문항 수) * 100
    return Math.min(Math.round(((index + 1) / totalCount) * 100), 100);
  };

  const handleBack = () => {
    const checkPagePath = `${prefix}/headache_check`; 
    const listPagePath = `${prefix}/check`;           

    const shouldGoToCheckPage = answers.length > 0 || hasPreviousHistory;

    if (shouldGoToCheckPage) {
      navigate(checkPagePath);
    } else {
      navigate(listPagePath, { replace: true });
    }
  };

  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
  };

  const handlePreviousAnswerClick = (questionId: string) => {
    const questionIndex = flattenedQuestions.findIndex(
      (q) => q.id === questionId
    );
    if (questionIndex !== -1) {
      setCurrentQuestionIndex(questionIndex);
      const previousAnswer = answers.find(
        (answer) => answer.questionId === questionId
      );
      if (previousAnswer) {
        setSelectedOption(previousAnswer.optionId);
      }
      setIsEditing(true);
    }
  };

  const handleBackToLatest = () => {
    const lastAnsweredIndex = answers.length - 1;
    if (lastAnsweredIndex >= 0) {
      const lastQuestion = flattenedQuestions.find(
        (q) => q.id === answers[lastAnsweredIndex].questionId
      );
      if (lastQuestion) {
        const lastQuestionIndex = flattenedQuestions.findIndex(
          (q) => q.id === lastQuestion.id
        );
        setCurrentQuestionIndex(lastQuestionIndex + 1);
        setSelectedOption(null);
        setIsEditing(false);
      }
    }
  };

  const saveAnswersToFirebase = async () => {
    try {
      setSavingToFirebase(true);

      // Calculate total score 
      const totalScore = answers.reduce((sum, answer) => {
        if (answer.value === true) {
          return sum + 1; 
        }
        return sum;
      }, 0);
      const userId = getUserId();

      // 저장할 데이터
      const testData = {
        testType: "headache",
        answers: answers,
        totalScore: totalScore,
        timestamp: serverTimestamp(), 
        userId: userId,
      };

      // 1단계에서 받아온 docId가 있는지 확인
      if (docId) {
        // docId가 있으면 (이어서 하는 경우) -> 'setDoc'으로 덮어쓰기
        const docRef = doc(db, "userHeadacheResponse", docId);
        await setDoc(docRef, testData, { merge: true }); // 기존 문서 업데이트
      } else {
        // docId가 없으면 (새 검사인 경우) -> 'addDoc'으로 새로 추가
        await addDoc(collection(db, "userHeadacheResponse"), testData);
      }
    } catch (error) {
      console.error("Error saving test results to Firebase:", error);
      throw error;
    } finally {
      setSavingToFirebase(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedOption) return;

    const currentQuestion = flattenedQuestions[currentQuestionIndex];
    const selectedOptionData = currentQuestion.options.find(
      (opt) => opt.optionTextKey === selectedOption
    );

    if (!selectedOptionData) return;

    const newAnswer = {
      questionId: currentQuestion.id,
      optionId: selectedOption,
      value: selectedOptionData.value,
    };

    // Check if this is an edit and if the answer is changing from "no" to "yes"
    const existingAnswer = answers.find(
      (answer) => answer.questionId === currentQuestion.id
    );
    const isChangingToYes =
      existingAnswer &&
      existingAnswer.value === false &&
      newAnswer.value === true;

    const updatedAnswers = [...answers];
    // Update or add the answer
    const existingAnswerIndex = answers.findIndex(
      (answer) => answer.questionId === currentQuestion.id
    );
    if (existingAnswerIndex !== -1) {
      updatedAnswers[existingAnswerIndex] = newAnswer;
    } else {
      updatedAnswers.push(newAnswer);
    }

    const nextFlattenedQuestions = flattenQuestions(
      headacheData.questions,
      updatedAnswers
    );

    setAnswers(updatedAnswers);
    setSelectedOption(null);

    if (isEditing) {
      // If changing from "no" to "yes", we need to recalculate the flattened questions
      // and then move to the next question
      if (isChangingToYes) {
        // The flattenedQuestions will be updated in the next render due to the useEffect
        // that depends on answers
        setFlattenedQuestions(nextFlattenedQuestions);
        setTimeout(() => {
          // Find the index of the next question after the current one
          const nextQuestionIndex = currentQuestionIndex + 1;
          if (nextQuestionIndex < nextFlattenedQuestions.length) {
            setCurrentQuestionIndex(nextQuestionIndex);
          }
        }, 0);
      } else {
        handleBackToLatest();
      }
    } else if (currentQuestionIndex < nextFlattenedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // All questions have been answered
      // Move past the last question so it shows as a selected answer
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setTestCompleted(true);

      // Save to Firebase
      try {
        setSavingToFirebase(true);
        const totalScore = updatedAnswers.reduce((sum, answer) => {
          if (answer.value === true) {
            return sum + 1;
          }
          return sum;
        }, 0);

        const userId = getUserId();
        const testData = {
          testType: "headache",
          answers: updatedAnswers,
          totalScore: totalScore,
          timestamp: serverTimestamp(),
          userId: userId,
        };
  
        if (docId) {
          const docRef = doc(db, "userHeadacheResponse", docId);
          await setDoc(docRef, testData, { merge: true });
        } else {
          await addDoc(collection(db, "userHeadacheResponse"), testData);
        }
        setSavingToFirebase(false);
      } catch (error) {
        console.error("Failed to save test results:", error);
        // Continue to results page even if saving fails
      }
    }
  };

  const handleViewResults = () => {
    // Calculate total score (only for numeric values)
    const totalScore = answers.reduce((sum, answer) => {
      if (typeof answer.value === "number") {
        return sum + 1;
      }
      return sum;
    }, 0);

    // sessionStorage에서 sid 가져오기
    const sessionId = sessionStorage.getItem("current_session_id");

    // Save answers to localStorage before navigating
    localStorage.setItem("headacheAnswers", JSON.stringify(answers));

    // Navigate to results page with score
    navigate(`${prefix}/headache/check/results`, {
      state: {
        score: totalScore,
        answers: answers,
        sessionId: sessionId
      },
    });
  };

  const getSelectedAnswer = (questionId: string) => {
    return answers.find((answer) => answer.questionId === questionId);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t("check_before_2")}</span>
      </div>
      <div className={styles.chatContainer} ref={chatContainerRef}>
        {flattenedQuestions.map((question, index) => {
          const selectedAnswer = getSelectedAnswer(question.id);
          const isCurrentQuestion = index === currentQuestionIndex;
          const hasAnswer = selectedAnswer !== undefined;

          // 진행률 계산
          const progress = getProgressPercentage(index, flattenedQuestions.length);

          if (index > currentQuestionIndex && !hasAnswer) return null;

          return (
            <React.Fragment key={question.id}>
              <div
                className={`${styles.botMessage} ${
                  !isCurrentQuestion ? styles.blurred : ""
                }`}
              >
                <div className={styles.messageContent}>
                  {/* 프로그래스 바 컨테이너 추가 */}
                  <div className={styles.progressContainer}>
                    <div className={styles.progressBarBackground}>
                      <div 
                        className={styles.progressBar} 
                        style={{ width: `${progress}%` }} 
                      />
                    </div>
                    <span className={styles.progressText}>{progress}%</span>
                  </div>

                  <div className={styles.questionText}>
                    {t(question.questionTextKey)}
                  </div>
                </div>
              </div>
              <div
                className={`${styles.userMessage} ${
                  !isCurrentQuestion ? styles.blurred : ""
                }`}
              >
                {isCurrentQuestion ? (
                  <>
                    <div
                      className={styles.optionsContainer}
                      ref={(el) => {
                        optionsContainerRefs.current[index] = el;
                      }}
                    >
                      {question.options.map((option) => (
                        <button
                          key={option.optionTextKey}
                          className={`${styles.optionButton} ${
                            selectedOption === option.optionTextKey
                              ? styles.selected
                              : ""
                          }`}
                          onClick={() =>
                            handleOptionSelect(option.optionTextKey)
                          }
                        >
                          {t(option.optionTextKey)}
                        </button>
                      ))}
                    </div>
                    <button
                      className={styles.confirmButton}
                      onClick={handleConfirm}
                      disabled={!selectedOption}
                    >
                      {t("confirm_button_text")}
                    </button>
                  </>
                ) : (
                  selectedAnswer && (
                    <div
                      className={styles.selectedAnswer}
                      onClick={() => handlePreviousAnswerClick(question.id)}
                    >
                      {t(selectedAnswer.optionId)}
                    </div>
                  )
                )}
              </div>
            </React.Fragment>
          );
        })}

        {testCompleted && (
          <div ref={resultsRef}>
            <div className={styles.botMessage}>
              <div className={styles.messageContent}>
                <div className={styles.questionText}>{t("test_result")}</div>
              </div>
            </div>
            <div className={styles.botMessage}>
              <button
                className={styles.viewResultsButton}
                onClick={handleViewResults}
                disabled={savingToFirebase}
              >
                {savingToFirebase ? t("saving") : t("view_results")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeadacheCheckNew;
